import React, { useState, useEffect } from 'react';
import {
  Layout,
  Typography,
  Button,
  Card,
  Input,
  Select,
  Flex,
  theme,
  Dropdown,
  Grid,
  message,
  Modal,
  Spin,
} from 'antd';
import {
  LeftOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  EllipsisOutlined,
  DownOutlined,
  UserOutlined,
  RobotOutlined,
  FileTextOutlined,
  UploadOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import './AssistantFlowNew.css';
import { useAccount } from '../../../../../store';
import { useAssistant } from '../../../../../contexts/AssistantContext';
import { NodeSelector } from './NodeSelector/nodeSelector';
import { IntegrationSelector } from './IntegrationSelector/IntegrationSelector';
import { CardIntegrationAction } from './CardIntegrationAction/CardIntegrationAction';
import { CardCustomAction } from './CardCustomAction/CardCustomAction';
import { CardCustomIntegrationAction } from './CardCustomIntegrationAction/CardCustomIntegrationAction';
import { CardQueueAction } from './CardQueueAction/CardQueueAction';
import { CardProcessFlowAction } from './CardProcessFlowAction/CardProcessFlowAction';
import { ReactComponent as BasicSilia } from '../../../../../assets/img/Basic-silia.svg';
import { ReactComponent as BasicEca } from '../../../../../assets/img/Basic-eca.svg';
import { useTheme } from '../../../../../hooks/useTheme';
import { MessageItem, NewComponentProps, IntegrationData, AppearanceState } from '../../../types';
import { myAssistantServices } from '../../../services/myAssistantServices';
import {
  getIntegrationId,
  getFunctionId,
  getFunctionName,
  extractActionInfo,
  formatConversationMessages,
  processAIFlowResponse,
  toColorString,
  getAppearanceStateFromAssistant,
} from '../../../utils';
import { Chatbot } from '../../../../../Components/Chatbot/Chatbot';
import { KnowledgeSources } from '../../../../../Components/MyAgent/Knowledge/KnowledgeSources';
import { Upload as AntUpload } from 'antd';

import { createLogger } from '../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Title, Text } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

const API_BASE_URL = process.env.REACT_APP_API_HOST || 'https://api.dev.mind.creai.cloud';

export const NewFlow: React.FC<NewComponentProps> = ({
  onReturnButtonClick,
  mode,
  flowData,
  conversationData,
  actionsData = {},
}) => {
  const { currentAccount } = useAccount();
  const { currentAssistant } = useAssistant();
  const { themeName } = useTheme();
  const chatbotId = currentAssistant?.id;

  const { token } = useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [flowName, setFlowName] = useState('');
  const [description, setDescription] = useState('');
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showNodeSelector, setShowNodeSelector] = useState(false);
  const [showIntegrationSelector, setShowIntegrationSelector] = useState(false);
  const [selectedNodeType, setSelectedNodeType] = useState<'conversation' | 'action' | null>(null);
  const [nodeSelectorValue, setNodeSelectorValue] = useState<string | null>(null);
  const [integrationsData, setIntegrationsData] = useState<IntegrationData>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [insertPosition, setInsertPosition] = useState<number | null>(null);
  const [actions, setActions] = useState<{ [key: string]: any }>({});
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [openNodeSelectorIndex, setOpenNodeSelectorIndex] = useState<number | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isEditMode = mode === 'edit';
  const [fileModalVisible, setFileModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const channelId = React.useMemo(() => uuidv4(), []);
  const [fileContent, setFileContent] = useState<string>('');
  const [appearanceState, setAppearanceState] = useState<AppearanceState>(
    getAppearanceStateFromAssistant(currentAssistant),
  );
  const [showPreviewButton, setShowPreviewButton] = useState(isEditMode);
  const [flowGeneratorConfig, setFlowGeneratorConfig] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Update appearance state when currentAssistant changes
  useEffect(() => {
    if (currentAssistant) {
      setAppearanceState(getAppearanceStateFromAssistant(currentAssistant));
    }
  }, [currentAssistant]);

  // Default cloud WebSocket URL
  const DEFAULT_WEBSOCKET_URL = process.env.REACT_APP_WS_SHARED_URL;
  const [flowChatbotId, setFlowChatbotId] = useState<string | null>(null);

  // Initialize chatbot: get ID by flowType, then get config
  useEffect(() => {
    const initializeChatbot = async () => {
      try {
        // Get chatbot ID dynamically by flowType
        const dynamicChatbotId = await myAssistantServices.getChatbotIdByFlowType('conversation');

        if (!dynamicChatbotId) {
          logger.error('[Flow Generator] No chatbot found for flowType "conversation"');
          return;
        }

        // Clear conversation history on reload
        localStorage.removeItem(`chatbot_messages_${dynamicChatbotId}`);
        localStorage.removeItem(`chatbot_greeting_shown_${dynamicChatbotId}`);

        setFlowChatbotId(dynamicChatbotId);

        // Get chatbot configuration
        const responseData = await myAssistantServices.getChatbotConfig(dynamicChatbotId);
        const webchatConfig = responseData.webchatConfig;
        const wsUrlToUse = DEFAULT_WEBSOCKET_URL || webchatConfig.wsUrl || process.env.REACT_APP_WS_URL;

        setFlowGeneratorConfig({
          botName: responseData.name || webchatConfig.botName || 'Flow Generator',
          colorMain: webchatConfig.colorMain,
          colorTitle: webchatConfig.colorTitle,
          iconBgColor: webchatConfig.iconBgColor || '#ffffff',
          wsUrl: wsUrlToUse,
        });
      } catch (error) {
        logger.error('[Flow Generator] Error initializing chatbot:', error);
      }
    };

    initializeChatbot();
  }, []);

  const fileUploadProps = {
    beforeUpload: (file: File) => {
      const isTxtOrPdf = file.type === 'text/plain' || file.type === 'application/pdf';
      if (!isTxtOrPdf) {
        message.error('You can only upload TXT or PDF files!');
        return AntUpload.LIST_IGNORE;
      }
      setSelectedFile(file);
      return false;
    },
    onRemove: () => {
      setSelectedFile(null);
    },
  };

  // Function to read file content when selected
  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setFileContent(content);
    };
    reader.readAsText(file);
  };

  // Function for Knowledge Files modal upload (same as Knowledge)
  const handleKnowledgeFileUpload = async () => {
    if (!selectedFile) {
      message.error('Please select a file to upload');
      return;
    }
    setIsFileUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const content = e.target!.result as string;
          const b64Content = content.split(',')[1];
          const payload = {
            type: 'file',
            files: [
              {
                filename: selectedFile.name,
                fileData: b64Content,
              },
            ],
          };

          const url = `${API_BASE_URL}/docMiner/knowledge?chatbotId=${chatbotId}`;
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            message.success('File uploaded successfully');
            setSelectedFile(null);
            setFileModalVisible(false);
          } else {
            throw new Error('Failed to upload file');
          }
        } catch (error) {
          logger.error('File upload error:', error);
          message.error(`Failed to upload file: ${error.message}`);
        } finally {
          setIsFileUploading(false);
        }
      };
      reader.onerror = (error) => {
        logger.error('FileReader error:', error);
        message.error('Failed to read file');
        setIsFileUploading(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      logger.error('File upload preparation error:', error);
      message.error(`Failed to upload file: ${error.message}`);
      setIsFileUploading(false);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    if (isEditMode && flowData) {
      setFlowName(flowData.name || '');
      setDescription(flowData.description || '');

      const mappedMessages =
        conversationData?.messages?.map((msg, index) => {
          const baseMessage: any = {
            id: Date.now() + index,
            text: msg.message,
            role: msg.role === 'user' ? 'User' : 'Virtual Agent',
            actionId: msg.actionId,
          };

          // Check if it's a Process Flow message and extract toolSchemaId
          if (msg.message && typeof msg.message === 'string' &&
              (msg.message.includes('Execute Process Flow:') || msg.message.includes('"type":"function"'))) {
            try {
              const processFlowRegex = /Execute Process Flow:\s*(\{.*\})/;
              const match = msg.message.match(processFlowRegex);
              if (match) {
                const toolSchemaData = JSON.parse(match[1]);
                // If the tool schema data has an id, store it as toolSchemaId
                if (toolSchemaData.id || toolSchemaData.toolSchemaId) {
                  baseMessage.toolSchemaId = toolSchemaData.id || toolSchemaData.toolSchemaId;
                }
              }
            } catch (error) {
              console.warn('Could not extract toolSchemaId from Process Flow message:', error);
            }
          }

          // Check if it's a Custom Integration message and extract toolSchemaId
          if (msg.message && typeof msg.message === 'string' && msg.message.includes('@custom/integration')) {
            try {
              const customIntegrationRegex = /@custom\/integration\s+(\{.*\})/;
              const match = msg.message.match(customIntegrationRegex);
              if (match) {
                const customIntegrationData = JSON.parse(match[1]);
                // If the custom integration data has a toolSchemaId, store it
                if (customIntegrationData.toolSchemaId) {
                  baseMessage.toolSchemaId = customIntegrationData.toolSchemaId;
                }
              }
            } catch (error) {
              console.warn('Could not extract toolSchemaId from Custom Integration message:', error);
            }
          }

          if (msg.message.startsWith('@')) {
            const integrationId = getIntegrationId(msg.message);
            const functionId = getFunctionId(msg.message);
            return {
              ...baseMessage,
              integrationId,
              functionId,
            };
          }

          return baseMessage;
        }) || [];

      setMessages(mappedMessages);
    } else {
      setFlowName('');
      setDescription('');
      setMessages([{ id: Date.now(), text: '', role: '' }]);
    }
  }, [mode, flowData, conversationData, isEditMode]);

  // Cargar datos de integraciones una sola vez
  useEffect(() => {
    const loadIntegrationsData = async () => {
      try {
        const integrations = await myAssistantServices.getIntegrations();
        const integrationMap = integrations.reduce((acc: IntegrationData, integration: any) => {
          acc[integration.id] = {
            logo: integration.logo || 'https://placehold.co/200x100?text=Default',
            name: integration.name,
            id: integration.id,
          };
          return acc;
        }, {});

        setIntegrationsData(integrationMap);
      } catch (error) {
        logger.error('Error loading integrations:', error);
      }
    };

    loadIntegrationsData();
  }, []);

  // Cargar acciones existentes
  useEffect(() => {
    const loadActions = async () => {
      if (!chatbotId || !isEditMode) return;

      try {
        if (Object.keys(actionsData).length > 0) {
          logger.info('Setting actions from actionsData:', actionsData);
          setActions(actionsData);
        } else {
          const actionsToLoad = new Set<string>();
          messages.forEach((msg) => {
            if (msg.functionId) {
              actionsToLoad.add(msg.functionId);
            }
          });

          if (actionsToLoad.size > 0) {
            const loadedActions: { [key: string]: any[] } = {};
            for (const functionId of actionsToLoad) {
              try {
                const actions = await myAssistantServices.getFlowActions(functionId);
                if (actions) {
                  loadedActions[functionId] = actions;
                }
              } catch (error) {
                logger.error(`Error loading actions for function ${functionId}:`, error);
              }
            }
            logger.info('Setting actions from loaded data:', loadedActions);
            setActions(loadedActions);
          }
        }
      } catch (error) {
        logger.error('Error loading actions:', error);
      }
    };

    loadActions();
  }, [chatbotId, actionsData, isEditMode, messages]);

  // Manejo de mensajes
  const handleAddMessage = () => {
    setMessages([...messages, { id: Date.now(), text: '', role: '' }]);
  };

  const handleRemoveMessage = (id: number) => {
    setMessages(messages.filter((msg) => msg.id !== id));
  };

  const handleChangeMessage = (id: number, value: string) => {
    setMessages(messages.map((msg) => (msg.id === id ? { ...msg, text: value } : msg)));
  };

  const handleChangeRole = (id: number, value: string) => {
    setMessages(messages.map((msg) => (msg.id === id ? { ...msg, role: value } : msg)));
  };

  const getCurrentDate = () => {
    const date = new Date();
    return date.toLocaleDateString('en-US');
  };

  const handleNodeSelect = (type: 'conversation' | 'action') => {
    setSelectedNodeType(type);
    setShowNodeSelector(false);
    setNodeSelectorValue(null);

    if (type === 'conversation') {
      if (insertPosition !== null) {
        const newMessage = { id: Date.now(), text: '', role: '' };
        const newMessages = [...messages];
        newMessages.splice(insertPosition, 0, newMessage);
        setMessages(newMessages);
      } else {
        handleAddMessage();
      }
    } else {
      setShowIntegrationSelector(true);
    }
  };

  const handleIntegrationSave = (message: string) => {
    // Extraer el ID de la integración del mensaje
    const integrationId = getIntegrationId(message);
    const functionId = getFunctionId(message);

    const newMessage = {
      id: Date.now(),
      text: message,
      role: 'Virtual Agent',
      integrationId: integrationId,
      functionId: functionId,
    };

    if (insertPosition !== null) {
      const newMessages = [...messages];
      newMessages.splice(insertPosition, 0, newMessage);
      setMessages(newMessages);
    } else {
      setMessages([...messages, newMessage]);
    }
    setShowIntegrationSelector(false);
    setInsertPosition(null);
  };

  // Renderizar el mensaje usando los IDs
  const renderMessage = (msg: MessageItem) => {
    // Verificar si es una asignación de cola
    if (msg.text && typeof msg.text === 'string' && msg.text.includes('@queue/assign')) {
      return (
        <Flex
          gap={token.marginSM}
          align="center"
          justify="center"
          style={{ width: '100%', marginBottom: token.marginMD }}
        >
          <CardQueueAction
            message={msg.text}
            onDelete={() => handleRemoveMessage(msg.id)}
            onDuplicate={() => handleDuplicateMessage(msg.id)}
            onEdit={(newMessage) => handleEditMessage(msg.id, newMessage)}
            chatbotId={chatbotId}
            flowId={flowData?.id}
          />
        </Flex>
      );
    }

    // Verificar si es un Process Flow
    if (msg.text && typeof msg.text === 'string' && (msg.text.includes('Execute Process Flow:') || msg.text.includes('"type":"processflow"'))) {
      return (
        <Flex
          gap={token.marginSM}
          align="center"
          justify="center"
          style={{ width: '100%', marginBottom: token.marginMD }}
        >
          <CardProcessFlowAction
            message={msg.text}
            onDelete={() => handleRemoveMessage(msg.id)}
            onDuplicate={() => handleDuplicateMessage(msg.id)}
            onEdit={(newMessage) => handleEditMessage(msg.id, newMessage)}
            assistantId={chatbotId || ''}
            flowId={flowData?.id}
          />
        </Flex>
      );
    }

    // Verificar si es una integración personalizada
    if (msg.text && typeof msg.text === 'string' && msg.text.includes('@custom/integration')) {
      return (
        <Flex
          gap={token.marginSM}
          align="center"
          justify="center"
          style={{ width: '100%', marginBottom: token.marginMD }}
        >
          <CardCustomAction
            message={msg.text}
            onDelete={() => handleRemoveMessage(msg.id)}
            onDuplicate={() => handleDuplicateMessage(msg.id)}
            onEdit={(newMessage) => handleEditMessage(msg.id, newMessage)}
            chatbotId={chatbotId}
            flowId={flowData?.id}
          />
        </Flex>
      );
    }

    // Extraer integrationId y functionId del mensaje para integraciones personalizadas
    const integrationId = getIntegrationId(msg.text);
    const functionId = getFunctionId(msg.text);

    // Si el mensaje tiene integrationId (incluyendo integraciones personalizadas), renderizarlo como CardIntegrationAction
    if (integrationId) {
      // Para integraciones personalizadas, crear datos de integración simulados
      let integrationData = integrationsData[integrationId];

      if (!integrationData) {
        // Es una integración personalizada, usar CardCustomIntegrationAction
        logger.info('Detected custom integration:', integrationId, 'message:', msg.text);
        return (
          <Flex
            gap={token.marginSM}
            align="center"
            justify="center"
            style={{ width: '100%', marginBottom: token.marginMD }}
          >
            <CardCustomIntegrationAction
              message={msg.text}
              onDelete={() => handleRemoveMessage(msg.id)}
              onDuplicate={() => handleDuplicateMessage(msg.id)}
              onEdit={(newMessage) => handleEditMessage(msg.id, newMessage)}
              chatbotId={chatbotId}
              flowId={flowData?.id}
            />
          </Flex>
        );
      }

      if (integrationData) {
        // Obtener las acciones disponibles para esta función
        const availableActions = functionId ? actions[functionId] || [] : [];
        logger.info('Available actions for function', functionId, ':', availableActions);

        // Si tenemos un actionId, buscar la acción específica
        let selectedAction = null;
        if (msg.actionId) {
          // Buscar la acción en las acciones disponibles para esta función
          selectedAction = availableActions.find((action: any) => action.id === msg.actionId);
          logger.info('Selected action:', selectedAction);
        }

        return (
          <Flex
            gap={token.marginSM}
            align="center"
            justify="center"
            style={{ width: '100%', marginBottom: token.marginMD }}
          >
            <CardIntegrationAction
              message={msg.text}
              onDelete={() => handleRemoveMessage(msg.id)}
              onDuplicate={() => handleDuplicateMessage(msg.id)}
              onEdit={(newMessage) => handleEditMessage(msg.id, newMessage)}
              integrationData={integrationData}
              availableActions={availableActions}
              onActionSelect={(actionId) => {
                setMessages(messages.map((m) => (m.id === msg.id ? { ...m, actionId } : m)));
              }}
              selectedActionId={msg.actionId}
              selectedAction={selectedAction}
              chatbotId={chatbotId}
              flowId={flowData?.id}
            />
          </Flex>
        );
      }
    }

    // Si no es un mensaje de integración, renderizar como mensaje normal
    return (
      <Flex
        gap={token.marginSM}
        align="center"
        vertical={isMobile}
        style={{ width: '100%', marginBottom: token.marginMD }}
      >
        <div style={{ flex: 1, position: 'relative' }}>
          <Input
            placeholder={`Message ${messages.indexOf(msg) + 1}`}
            value={msg.text || ''}
            onChange={(e) => handleChangeMessage(msg.id, e.target.value)}
            maxLength={1000000}
            style={{ width: '100%' }}
          />
          <span style={{
            position: 'absolute',
            bottom: '-20px',
            right: '11px',
            fontSize: '12px',
            color: 'rgba(0, 0, 0, 0.45)',
            pointerEvents: 'none',
          }}>
            {(msg.text || '').length}/1000000
          </span>
        </div>
        <Select
          placeholder="Select Role"
          suffixIcon={<DownOutlined style={{ color: '#000' }} />}
          value={msg.role || undefined}
          style={{ width: 140 }}
          options={[
            {
              value: 'User',
              label: (
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  User
                </span>
              ),
            },
            {
              value: 'Virtual Agent',
              label: (
                <span>
                  <RobotOutlined style={{ marginRight: 8 }} />
                  Virtual Agent
                </span>
              ),
            },
          ]}
          onChange={(value) => handleChangeRole(msg.id, value)}
        />
        <Dropdown
          menu={{
            items: [
              {
                key: 'delete',
                label: 'Delete',
                onClick: () => handleRemoveMessage(msg.id),
              },
            ],
          }}
        >
          <Button icon={<EllipsisOutlined />} />
        </Dropdown>
      </Flex>
    );
  };

  // Función para duplicar un mensaje
  const handleDuplicateMessage = (id: number) => {
    const messageToClone = messages.find((msg) => msg.id === id);
    if (!messageToClone) return;

    const messageIndex = messages.findIndex((msg) => msg.id === id);
    const newMessages = [...messages];

    // Crear una copia completa del mensaje incluyendo todas las propiedades
    const duplicatedMessage = {
      id: Date.now(),
      text: messageToClone.text,
      role: messageToClone.role,
      integrationId: messageToClone.integrationId,
      functionId: messageToClone.functionId,
    };

    // Insertar el mensaje duplicado después del original
    newMessages.splice(messageIndex + 1, 0, duplicatedMessage);

    // Actualizar los mensajes
    setMessages(newMessages);
    message.success('Action duplicated successfully');
  };

  // Agregar función para manejar la edición
  const handleEditMessage = (id: number, newMessage: string) => {
    setMessages(messages.map((msg) => (msg.id === id ? { ...msg, text: newMessage } : msg)));
  };

  // Agregar manejador para cuando se cierra el modal
  const handleIntegrationModalClose = () => {
    setShowIntegrationSelector(false);
    setInsertPosition(null);
  };

  // Guardar flujo + conversación
  const handleSave = async () => {
    if (!flowName.trim()) {
      message.error('Flow name is required');
      return;
    }

    if (!chatbotId) {
      message.error('Chatbot ID is required');
      return;
    }

    setIsSaving(true);

    try {
      // STEP 1: Process and save all Process Flow and Custom Integration tool schemas FIRST
      const updatedMessages = [...messages];

      // Helper function to extract parameters from custom integration code
      const extractParametersFromCode = (code: string): Record<string, any> => {
        const detectedParams: Record<string, any> = {};
        
        if (!code || typeof code !== 'string') {
          return detectedParams;
        }

        // Pattern 1: parameters.paramName or parameters['paramName'] or parameters["paramName"]
        const dotNotationPattern = /parameters\.([a-zA-Z_$][a-zA-Z0-9_$]*)/g;
        const bracketNotationPattern = /parameters\[['"]([^'"]+)['"]\]/g;
        
        // Extract from dot notation: parameters.cel, parameters.apiKey, etc.
        let match;
        while ((match = dotNotationPattern.exec(code)) !== null) {
          const paramName = match[1];
          if (paramName && !detectedParams.hasOwnProperty(paramName)) {
            detectedParams[paramName] = '';
          }
        }
        
        // Extract from bracket notation: parameters['CNROCEL'], parameters["CMESSAG"], etc.
        while ((match = bracketNotationPattern.exec(code)) !== null) {
          const paramName = match[1];
          if (paramName && !detectedParams.hasOwnProperty(paramName)) {
            detectedParams[paramName] = '';
          }
        }
        
        return detectedParams;
      };

      // Helper function to detect parameter type from name
      const detectParameterType = (paramName: string): string => {
        const lowerParam = paramName.toLowerCase();
        if (lowerParam.includes('id') || lowerParam.includes('num') || lowerParam.includes('cel') || 
            lowerParam.includes('count') || lowerParam.includes('limit') || lowerParam.includes('page')) {
          return 'number';
        } else if (lowerParam.includes('enabled') || lowerParam.includes('active') || lowerParam.includes('flag')) {
          return 'boolean';
        }
        return 'string';
      };

      for (let i = 0; i < updatedMessages.length; i++) {
        const msg = updatedMessages[i];

        // Check if this is a Process Flow message
        if (msg.text && typeof msg.text === 'string' &&
            (msg.text.includes('Execute Process Flow:') || msg.text.includes('"type":"function"'))) {
          try {
            // Extract the tool schema from the message
            const processFlowRegex = /Execute Process Flow:\s*(\{.*\})/;
            const match = msg.text.match(processFlowRegex);

            if (match) {
              const toolSchemaData = JSON.parse(match[1]);

              // Check if this tool schema already has an ID (for updates)
              const existingToolSchemaId = (msg as any).toolSchemaId;

              let toolSchemaResponse;

              if (existingToolSchemaId) {
                // Update existing tool schema
                console.log(`Updating tool schema ${existingToolSchemaId}...`);
                toolSchemaResponse = await myAssistantServices.updateToolSchema(
                  existingToolSchemaId,
                  toolSchemaData
                );
              } else {
                // Create new tool schema
                console.log('Creating new tool schema...');
                toolSchemaResponse = await myAssistantServices.createToolSchema(toolSchemaData);
              }

              // Store the tool schema ID in the message for future updates
              if (toolSchemaResponse?.id) {
                (updatedMessages[i] as any).toolSchemaId = toolSchemaResponse.id;

                // Update the message text to include the toolSchemaId for persistence
                toolSchemaData.id = toolSchemaResponse.id;
                toolSchemaData.toolSchemaId = toolSchemaResponse.id;
                const updatedMessageText = `Execute Process Flow: ${JSON.stringify(toolSchemaData)}`;
                updatedMessages[i].text = updatedMessageText;
              }
            }
          } catch (toolSchemaError) {
            console.error('Error saving tool schema for message:', msg, toolSchemaError);
            message.error(`Failed to save tool schema. Flow not saved. Error: ${toolSchemaError.message || toolSchemaError}`);
            return; // Stop flow save if tool schema fails
          }
        }

        // Check if this is a Custom Integration message
        if (msg.text && typeof msg.text === 'string' && msg.text.includes('@custom/integration')) {
          try {
            // Extract the custom integration data from the message
            const customIntegrationRegex = /@custom\/integration\s+(\{.*\})/;
            const match = msg.text.match(customIntegrationRegex);

            if (match) {
              const customIntegrationData = JSON.parse(match[1]);
              const integrationId = customIntegrationData.integrationId;
              const generatedCode = customIntegrationData.generatedCode || '';

              if (!integrationId) {
                console.warn('Custom integration message missing integrationId:', customIntegrationData);
                continue;
              }

              // Extract parameters from the generated code
              const detectedParams = extractParametersFromCode(generatedCode);

              // Build properties object with required engine and integrationId
              const properties: any = {
                engine: {
                  description: "Motor de ejecución",
                  type: "string",
                  enum: ["custom"]
                },
                integrationId: {
                  description: "ID de la integración personalizada",
                  type: "string",
                  enum: [integrationId]
                }
              };

              // Add detected parameters to properties
              const requiredFields = ["engine", "integrationId"];
              Object.keys(detectedParams).forEach(paramName => {
                const paramType = detectParameterType(paramName);
                properties[paramName] = {
                  description: `Parámetro: ${paramName}`,
                  type: paramType
                };
                // Optionally add to required fields (you can make this configurable)
                // requiredFields.push(paramName);
              });

              // Build OpenAI function calling format
              const functionName = `custom_integration_${integrationId.substring(0, 8)}`;
              const toolSchemaData = {
                chatbotId: chatbotId,
                type: "function",
                name: functionName,
                function: {
                  name: functionName,
                  description: customIntegrationData.prompt || `Custom integration: ${integrationId}`,
                  parameters: {
                    type: "object",
                    properties: properties,
                    required: requiredFields
                  }
                }
              };

              // Check if this tool schema already has an ID (for updates)
              const existingToolSchemaId = (msg as any).toolSchemaId;

              let toolSchemaResponse;

              if (existingToolSchemaId) {
                // Update existing tool schema
                console.log(`Updating custom integration tool schema ${existingToolSchemaId}...`);
                toolSchemaResponse = await myAssistantServices.updateToolSchema(
                  existingToolSchemaId,
                  toolSchemaData
                );
              } else {
                // Create new tool schema
                console.log('Creating new custom integration tool schema...');
                toolSchemaResponse = await myAssistantServices.createToolSchema(toolSchemaData);
              }

              // Store the tool schema ID in the message for future updates
              if (toolSchemaResponse?.id) {
                (updatedMessages[i] as any).toolSchemaId = toolSchemaResponse.id;
                
                // Update the message text to include the toolSchemaId for persistence
                customIntegrationData.toolSchemaId = toolSchemaResponse.id;
                const updatedMessageText = `@custom/integration ${JSON.stringify(customIntegrationData)}`;
                updatedMessages[i].text = updatedMessageText;
                
                console.log(`✅ Custom integration tool schema created/updated: ${toolSchemaResponse.id}`);
              }
            }
          } catch (toolSchemaError) {
            console.error('Error saving custom integration tool schema for message:', msg, toolSchemaError);
            message.error(`Failed to save custom integration tool schema. Flow not saved. Error: ${toolSchemaError.message || toolSchemaError}`);
            return; // Stop flow save if tool schema fails
          }
        }
      }

      // STEP 2: Now proceed with flow save
      const flowBody = {
        name: flowName,
        description,
        chatbotId,
        status: 0,
      };

      let flowId = flowData?.id;

      if (isEditMode && flowId) {
        await myAssistantServices.updateFlow(flowId, flowBody);
        message.success('Flow updated successfully');
      } else {
        const response = await myAssistantServices.createFlow(flowBody);
        flowId = response?.id;
        if (!flowId) throw new Error('Failed to retrieve flowId');
        message.success('Flow created successfully');
      }

      // STEP 3: Process integration actions
      for (let i = 0; i < updatedMessages.length; i++) {
        const msg = updatedMessages[i];
        if (msg.integrationId && msg.functionId) {
          try {
            const functionInfo = extractActionInfo(msg.text);
            if (!functionInfo) {
              logger.warn('No function info found in message:', msg);
              continue;
            }

            if (!functionInfo.parameters || !functionInfo.authId) {
              logger.warn('Missing required data in function info:', functionInfo);
              continue;
            }

            const actionData = {
              integrationFunctionId: msg.functionId,
              authId: functionInfo.authId,
              parameters: functionInfo.parameters,
              chatbotId,
              flowId,
            };

            let actionResponse;
            if (msg.actionId) {
              actionResponse = await myAssistantServices.updateFlowAction(msg.actionId, actionData);
            } else {
              actionResponse = await myAssistantServices.createFlowAction(actionData);
            }

            if (actionResponse?.id) {
              const updatedFunctionInfo = {
                ...functionInfo,
                actionId: actionResponse.id,
                flowId,
              };

              const updatedMessage = `@${msg.integrationId}/${msg.functionId}${JSON.stringify(updatedFunctionInfo)}`;
              updatedMessages[i] = {
                ...msg,
                text: updatedMessage,
                actionId: actionResponse.id,
              };
            }
          } catch (error) {
            logger.error('Error processing action for message:', msg, error);
            message.error(`Failed to save action for message ${i + 1}`);
          }
        }
      }

      setMessages(updatedMessages);

      const conversationMessages = formatConversationMessages(updatedMessages);

      if (isEditMode) {
        await myAssistantServices.updateFlowConversation(flowId, conversationMessages);
      } else {
        await myAssistantServices.createFlowConversation(flowId, conversationMessages);
      }


      setShowPreviewButton(true);
      if (isEditMode) {
        onReturnButtonClick();
      }
    } catch (error) {
      logger.error('Error saving flow:', error);
      message.error('Error saving flow');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportFlow = () => {
    try {
      // Generate the conversation flow text
      let exportText = `CONVERSATIONAL FLOW EXPORT\n`;
      exportText += `================================\n\n`;
      exportText += `Flow Name: ${flowName || 'Untitled Flow'}\n`;
      exportText += `Description: ${description || 'No description provided'}\n`;
      exportText += `Export Date: ${new Date().toLocaleString()}\n`;
      exportText += `Total Messages: ${messages.length}\n\n`;
      exportText += `CONVERSATION FLOW:\n`;
      exportText += `==================\n\n`;

      messages.forEach((msg, index) => {
        const messageNumber = index + 1;
        const role = msg.role || 'Unknown';
        const text = msg.text || '[Empty message]';

        exportText += `${messageNumber}. ${role.toUpperCase()}\n`;
        exportText += `   ${text}\n\n`;

        // Add integration info if present
        if (msg.integrationId) {
          const integrationData = integrationsData[msg.integrationId];
          exportText += `   [INTEGRATION: ${integrationData?.name || 'Unknown'}]\n`;
          if (msg.functionId) {
            exportText += `   [FUNCTION ID: ${msg.functionId}]\n`;
          }
          exportText += `\n`;
        }
      });

      exportText += `\nEND OF FLOW\n`;
      exportText += `Generated by Silia Assistant Builder`;

      // Create and download the file
      const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${flowName || 'conversational-flow'}-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success('Flow exported successfully!');
    } catch (error) {
      logger.error('Error exporting flow:', error);
      message.error('Failed to export flow');
    }
  };

  const handleGenerateAI = async () => {
    try {
      // Validate that at least one input is provided
      const hasPrompt = aiPrompt && aiPrompt.trim().length > 0;
      const hasFile = fileContent && fileContent.trim().length > 0;

      if (!hasPrompt && !hasFile) {
        message.warning('Please enter a description or upload a file first');
        return;
      }

      if (!chatbotId) {
        message.error('Chatbot ID is required');
        return;
      }

      setIsGenerating(true);

      // Prepare the data to send - only send non-empty values
      const promptToSend = hasPrompt ? aiPrompt.trim() : '';
      const fileToSend = hasFile ? fileContent.trim() : '';

      // Debug: log what we're sending
      logger.info('Sending to backend:', {
        prompt: promptToSend || '[empty]',
        chatbotId,
        fileContent: fileToSend ? `${fileToSend.substring(0, 100)}...` : '[empty]',
        fileContentLength: fileToSend?.length || 0,
        hasPrompt,
        hasFile,
      });

      // Send prompt, chatbotId and file content directly
      const data = await myAssistantServices.generateAIFlow(promptToSend, chatbotId, fileToSend);
      logger.info('AI Response:', data);

      const {
        flowName: newFlowName,
        description: newDescription,
        messages: newMessages,
      } = processAIFlowResponse(data, flowName, description);

      setFlowName(newFlowName);
      setDescription(newDescription);

      setMessages((prevMessages) => {
        if (prevMessages.length === 0 || prevMessages[0].text === '') {
          return newMessages;
        }
        return prevMessages;
      });

      message.success('AI flow generated successfully');
    } catch (error) {
      logger.error('Error generating AI response:', error);
      message.error('Failed to generate AI response');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveCustomIntegration = async (generatedCode: string, metadata: any) => {
    try {
      if (!chatbotId) {
        message.error('Chatbot ID is required');
        return;
      }

      const integrationName = `Custom Integration ${new Date().toLocaleString()}`;

      const response = await myAssistantServices.saveCustomIntegration({
        integrationName,
        generatedCode,
        chatbotId,
        flowId: flowData?.id,
        metadata,
      });

      if (response.id) {
        message.success('Custom integration saved successfully');
        // You can add additional logic here, like refreshing the integrations list
      }
    } catch (error) {
      logger.error('Error saving custom integration:', error);
      message.error('Failed to save custom integration');
    }
  };

  // Handler for automatic artifact acceptance from chatbot
  const handleArtifactAccept = async (artifact: { content: string, language?: string }) => {
    try {
      // Parse the artifact content (expecting flow JSON format)
      const parsedData = JSON.parse(artifact.content);
      logger.info('Parsed data:', parsedData);
      // Use the existing processAIFlowResponse utility to transform the data
      const {
        flowName: newFlowName,
        description: newDescription,
        messages: newMessages,
      } = processAIFlowResponse({ response: parsedData }, flowName, description);

      // Update the flow fields immediately
      setFlowName(newFlowName);
      setDescription(newDescription);

      // Update messages - always replace with new artifact content
      setMessages(newMessages);

      message.success('Flow configuration applied from artifact successfully');
    } catch (error) {
      logger.error('Error processing artifact:', error);
      message.error('Invalid artifact format. Expected flow JSON configuration.');
    }
  };

  return (
    <Layout style={{ background: token.colorBgContainer, overflowX: 'hidden' }}>
      <div
        style={{
          padding: isMobile ? token.paddingSM : '24px',
          position: 'relative',
        }}
      >
        <Flex vertical gap={isMobile ? token.marginSM : token.marginMD}>

          <Flex
            justify="space-between"
            align="center"
            vertical={isMobile}
            gap={isMobile ? token.marginSM : 0}
          >
            <Flex align="center" gap={token.marginXS}>
              {!editingTitle ? (
                <>
                  <Title level={4} style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
                    {flowName || 'Sin nombre'}
                  </Title>
                  <EditOutlined className="edit-icon" onClick={() => setEditingTitle(true)} />
                </>
              ) : (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Input
                    autoFocus
                    value={flowName}
                    placeholder="Enter flow name"
                    onBlur={() => setEditingTitle(false)}
                    onPressEnter={() => setEditingTitle(false)}
                    onChange={(e) => setFlowName(e.target.value)}
                    maxLength={30}
                    style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      lineHeight: '1.4',
                      padding: '4px 11px',
                      width: flowName ? `${Math.max(flowName.length * 0.6, 12)}em` : '200px',
                      maxWidth: '300px',
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    bottom: '-20px',
                    right: '11px',
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.45)',
                    pointerEvents: 'none',
                  }}>
                    {flowName.length}/30
                  </span>
                </div>
              )}
            </Flex>
            <Flex
              justify="end"
              align="center"
              gap={token.marginSM}
              style={{ marginBottom: token.marginLG }}
            >
              <Button
                icon={<ExportOutlined />}
                onClick={handleExportFlow}
                type="default"
                id="export-flow-button"
              >
                Export
              </Button>
              {/*               {showPreviewButton && (
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => setIsPreviewOpen(true)}
                  id="preview-flow-button"
                >
                  Preview
                </Button>
              )} */}
              <Button
                type="primary"
                onClick={handleSave}
                id="save-flow-button"
                loading={isSaving}
                disabled={isSaving}
              >
                {isSaving ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Flow' : 'Save')}
              </Button>
            </Flex>
          </Flex>

          <Flex align="center" gap={token.marginXS} style={{ maxWidth: '1200px' }}>
            {!editingDescription ? (
              <>
                <Text style={{ fontSize: isMobile ? 14 : 16, color: token.colorTextDescription }}>
                  {description || 'Description'}
                </Text>
                <EditOutlined className="edit-icon" onClick={() => setEditingDescription(true)} />
              </>
            ) : (
              <Input.TextArea
                autoFocus
                value={description}
                placeholder="Describe el flujo..."
                onBlur={() => setEditingDescription(false)}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={400}
                showCount
                autoSize={{ minRows: 2, maxRows: 6 }}
                style={{
                  // ancho proporcional al contenido, con un mínimo y máximo
                  width: '100%',
                  color: token.colorTextDescription,
                }}
              />
            )}
          </Flex>

          <Text type="secondary">Draft Last edited: {getCurrentDate()}</Text>

          {/* Card for generating conversation with AI */}
          <Flex
            style={{ flexDirection: 'column', maxWidth: '1000px', margin: '0 auto', width: '100%' }}
          >
            <Card
              title="Describe An Ideal Conversational Flow"
              style={{ width: '100%' }}
              bodyStyle={{ padding: isMobile ? token.paddingSM : token.padding }}
            >
              <Text type="secondary">
                Describe the conversation flow you want to create (e.g., "Create a flow for handling
                customer password reset requests").
              </Text>
              {/* Card Files igual a Knowledge, debajo del texto descriptivo */}
              <div style={{ margin: '24px 0' }}>
                {/* <KnowledgeSources
                  sources={[
                    {
                      title: 'Files',
                      description:
                        'Upload documents and automatically generate conversational flows based on their content.',
                      icon: (
                      
                        <FileTextOutlined
                          style={{ fontSize: '16px', color: 'var(--ant-colorIcon)' }}
                        />
                      ),
                      action: 'Upload',
                      onClick: () => setFileModalVisible(true),
                      iconBackground: theme.defaultConfig.token.colorPrimary,
                    },
                  ]}
                  isMobile={isMobile}
                /> */}
              </div>
              {/* Input y botón en una fila */}
              <Card style={{ marginBottom: token.marginLG }}>
                {flowGeneratorConfig && flowChatbotId ? (
                  <Chatbot
                    wsUrl={flowGeneratorConfig.wsUrl}
                    chatbotId={flowChatbotId}
                    channelId={channelId}
                    urlApi={`${process.env.REACT_APP_API_HOST}/chatbot`}
                    isWeb={true}
                    isPreview={false}
                    primaryColor={flowGeneratorConfig.colorTitle}
                    headerColor={flowGeneratorConfig.colorMain}
                    iconColor={flowGeneratorConfig.iconBgColor}
                    botName={flowGeneratorConfig.botName}
                    onArtifactAccept={handleArtifactAccept}
                    autoAcceptArtifacts={false}
                  />
                ) : (
                  <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
                    <Spin size="large" tip="Loading Flow Generator..." />
                  </Flex>
                )}
                {/* <Title level={5}>Generate with AI</Title>
                <Text type="secondary" style={{ marginBottom: token.marginMD }}>
                  Describe the flow you want to create or upload a conversation file
                </Text>
                <Flex gap={token.marginSM} style={{ marginTop: token.marginMD }} align="center">
                  <Input
                    placeholder="E.g., Create a flow for handling customer password reset requests"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    disabled={isGenerating}
                    style={{ flex: 1 }}
                  />
                  <Button
                    type="primary"
                    icon={
                      isGenerating ? (
                        <Spin size="small" />
                      ) : themeName === 'silia' ? (
                        <BasicSilia width={16} height={16} />
                      ) : (
                        <BasicEca width={16} height={16} />
                      )
                    }
                    onClick={handleGenerateAI}
                    disabled={isGenerating || isFileUploading}
                    style={{ minWidth: 180 }}
                    id="generate-flow-ai-button"
                  >
                    {isGenerating ? 'Generating...' : 'Generate with AI'}
                  </Button>
                </Flex> */}
              </Card>
            </Card>

            <Title
              level={5}
              className="add-conversation-flow-title"
              style={{ padding: '16px 0px' }}
            >
              Add conversational flow
            </Title>

            <div
              style={{
                padding: '0 16px',
                marginBottom: token.marginMD,
              }}
            >
              {messages.map((msg, index) => (
                <React.Fragment key={msg.id}>
                  {renderMessage(msg)}
                  <Flex justify="center" style={{ marginBottom: token.marginMD }}>
                    <Dropdown
                      open={openNodeSelectorIndex === index}
                      onOpenChange={(visible) => {
                        if (visible) {
                          setOpenNodeSelectorIndex(index);
                          setInsertPosition(index + 1);
                        } else {
                          setOpenNodeSelectorIndex(null);
                          setNodeSelectorValue(null);
                          setInsertPosition(null);
                        }
                      }}
                      dropdownRender={() => (
                        <div
                          style={{
                            backgroundColor: token.colorBgContainer,
                            boxShadow: token.boxShadow,
                          }}
                        >
                          <NodeSelector
                            onSelect={(type) => {
                              handleNodeSelect(type);
                              setOpenNodeSelectorIndex(null); // Cierra el dropdown al seleccionar
                            }}
                            selectedValue={nodeSelectorValue}
                          />
                        </div>
                      )}
                      trigger={['click']}
                      placement="bottomCenter"
                    >
                      <Button
                        icon={<PlusOutlined />}
                        style={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        shape="circle"
                        onClick={() => {
                          setOpenNodeSelectorIndex(index);
                          setInsertPosition(index + 1);
                        }}
                      />
                    </Dropdown>
                  </Flex>
                </React.Fragment>
              ))}
            </div>
          </Flex>

          <Modal
            open={showIntegrationSelector}
            onCancel={handleIntegrationModalClose}
            footer={null}
            width={800}
            destroyOnClose={true}
          >
            <IntegrationSelector
              onSave={(message) => {
                handleIntegrationSave(message);
                handleIntegrationModalClose();
              }}
              chatbotId={chatbotId}
              flowId={flowData?.id}
            />
          </Modal>

          {/* Modal de upload de archivo para Generate AI */}
          <Modal
            title="Upload Conversation File"
            open={fileModalVisible}
            onCancel={() => setFileModalVisible(false)}
            width={isMobile ? '95%' : '70%'}
            style={{ maxWidth: 1000 }}
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <Button
                  key="select"
                  type="primary"
                  onClick={() => {
                    if (selectedFile && fileContent) {
                      setFileModalVisible(false);
                      message.success(`File "${selectedFile.name}" selected for AI generation`);
                    } else {
                      message.warning('Please select a file first');
                    }
                  }}
                  disabled={!selectedFile || !fileContent}
                >
                  Use File for AI Generation
                </Button>
                <Button
                  key="cancel"
                  onClick={() => setFileModalVisible(false)}
                  style={{ marginLeft: 8 }}
                >
                  Cancel
                </Button>
              </div>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Upload a TXT file containing a conversation to generate a flow based on the dialogue
                patterns.
              </Text>
            </div>
            <AntUpload.Dragger
              accept=".txt"
              showUploadList={false}
              beforeUpload={(file) => {
                handleFileSelect(file);
                return false;
              }}
              style={{
                backgroundColor: selectedFile ? '#f6ffed' : undefined,
                borderColor: selectedFile ? '#b7eb8f' : undefined,
              }}
            >
              {selectedFile ? (
                <div>
                  <p className="ant-upload-drag-icon">
                    <FileTextOutlined style={{ color: '#52c41a' }} />
                  </p>
                  <p className="ant-upload-text" style={{ color: '#52c41a' }}>
                    ✓ {selectedFile.name} ({Math.round(fileContent.length / 1024)}KB)
                  </p>
                  <p className="ant-upload-hint">File ready for AI generation</p>
                </div>
              ) : (
                <div>
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to this area to upload</p>
                  <p className="ant-upload-hint">
                    Support for TXT files containing conversation data
                  </p>
                </div>
              )}
            </AntUpload.Dragger>
            {selectedFile && fileContent && (
              <div
                style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}
              >
                <Text strong>File Preview:</Text>
                <div
                  style={{
                    marginTop: 8,
                    maxHeight: 200,
                    overflow: 'auto',
                    fontSize: 12,
                    fontFamily: 'monospace',
                  }}
                >
                  {fileContent.substring(0, 500)}
                  {fileContent.length > 500 && '...'}
                </div>
              </div>
            )}
          </Modal>
        </Flex>
      </div>
      {isPreviewOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
          }}
        >
          <Chatbot
            wsUrl={currentAssistant?.websocketUrl || DEFAULT_WEBSOCKET_URL}
            chatbotId={currentAssistant?.id || '0'}
            channelId="preview"
            urlApi={`${process.env.REACT_APP_API_HOST}/chatbot`}
            isWeb={false}
            isPreview={true}
            primaryColor={toColorString(appearanceState.primaryColor)}
            headerColor={toColorString(appearanceState.headerColor)}
            iconColor={toColorString(appearanceState.iconColor)}
            botName={currentAssistant?.name}
          />
        </div>
      )}
    </Layout>
  );
};
